import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NavBar } from '../components/NavBar';
import './NewSupplier.css';

const SUPPLIER_TYPES = ['API', 'Manual', 'API + Manual', 'Panel Build', 'Specialty'];
const GEO_OPTIONS = ['US', 'UK', 'Canada', 'Australia', 'EU', 'APAC', 'LATAM', 'MENA', 'Global'];

export function NewSupplier() {
  const [formData, setFormData] = useState({
    name: '',
    status: 'Identified',
    supplierType: 'API',
    geoCoverage: [],
    primaryContactName: '',
    primaryContactEmail: ''
  });

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const { getToken } = useAuth();
  const navigate = useNavigate();

  function handleChange(field, value) {
    setFormData({ ...formData, [field]: value });
  }

  function handleGeoToggle(geo) {
    const current = formData.geoCoverage;
    setFormData({
      ...formData,
      geoCoverage: current.includes(geo) ? current.filter(g => g !== geo) : [...current, geo]
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Supplier name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch('/api/suppliers', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create supplier');
      }

      const data = await response.json();
      navigate(`/suppliers/${data.supplier.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page-container">
      <NavBar />
      <div className="new-supplier-container">
      <div className="new-supplier-header">
        <button className="back-button" onClick={() => navigate('/suppliers')}>
          ← Back to Suppliers
        </button>
        <h2>Add Supplier</h2>
        <p className="new-supplier-hint">Add the basics now — fill in full details on the supplier page.</p>
      </div>

      <form onSubmit={handleSubmit} className="new-supplier-form">

        <div className="form-section">
          <div className="form-field">
            <label>Company Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => handleChange('name', e.target.value)}
              placeholder="e.g. Dynata, Lucid, Cint"
              autoFocus
            />
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>Stage</label>
              <select value={formData.status} onChange={e => handleChange('status', e.target.value)}>
                {['Identified', 'Intro', 'Vetting', 'Pilot', 'Active'].map(s =>
                  <option key={s} value={s}>{s}</option>
                )}
              </select>
            </div>

            <div className="form-field">
              <label>Supplier Type</label>
              <select value={formData.supplierType} onChange={e => handleChange('supplierType', e.target.value)}>
                {SUPPLIER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="form-field">
            <label>Geographic Coverage</label>
            <div className="geo-checkbox-row">
              {GEO_OPTIONS.map(geo => (
                <label key={geo} className="geo-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.geoCoverage.includes(geo)}
                    onChange={() => handleGeoToggle(geo)}
                  />
                  {geo}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Primary Contact <span className="optional">optional</span></h3>
          <div className="form-row">
            <div className="form-field">
              <label>Name</label>
              <input
                type="text"
                value={formData.primaryContactName}
                onChange={e => handleChange('primaryContactName', e.target.value)}
                placeholder="Jane Smith"
              />
            </div>
            <div className="form-field">
              <label>Email</label>
              <input
                type="email"
                value={formData.primaryContactEmail}
                onChange={e => handleChange('primaryContactEmail', e.target.value)}
                placeholder="jane@supplier.com"
              />
            </div>
          </div>
        </div>

        {error && <div className="form-error">{error}</div>}

        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={() => navigate('/suppliers')}>
            Cancel
          </button>
          <button type="submit" className="button-primary" disabled={saving}>
            {saving ? 'Adding...' : 'Add Supplier →'}
          </button>
        </div>
      </form>
    </div>
    </div>
  );
}
